/**
 * Utility functions for handling product history notes
 *
 * Notes can be either:
 * - String (legacy format)
 * - Object with { message: string, template?: string, metadata?: object }
 */

export interface NotesData {
  message?: string;
  template?: string;
  metadata?: Record<string, any>;
}

/**
 * Extract human-readable message from notes (for backward compatibility only)
 *
 * NOTE: New data should NOT have message field. Use generateHistoryMessage() instead
 * which builds messages from eventType + metadata + newValue/previousValue.
 *
 * @param notes - String (legacy) or NotesData object
 * @returns Human-readable string message (only for legacy data)
 */
export function formatNotesMessage(
  notes?: string | NotesData | Record<string, any>,
): string | undefined {
  if (notes == null) {
    return undefined;
  }

  // Handle string (legacy format)
  if (typeof notes === 'string') {
    return notes;
  }

  // Handle object format - check for legacy message field (shouldn't exist in new data)
  if (typeof notes === 'object') {
    // Legacy format: check for message field
    if ('message' in notes && typeof (notes as any).message === 'string') {
      return (notes as any).message;
    }

    // Legacy format: check metadata.legacyMessage (temporary during migration)
    const metadata = (notes as NotesData)?.metadata;
    if (metadata?.legacyMessage && typeof metadata.legacyMessage === 'string') {
      return metadata.legacyMessage;
    }
  }

  // New format: no message stored - should use generateHistoryMessage() instead
  return undefined;
}

/**
 * Check if notes has structured metadata
 *
 * @param notes - String or NotesData object
 * @returns True if notes contains metadata
 */
export function hasNotesMetadata(notes?: string | NotesData | Record<string, any>): boolean {
  if (notes == null || typeof notes !== 'object') {
    return false;
  }

  return 'metadata' in notes && (notes as any).metadata != null;
}

/**
 * Get metadata from notes
 *
 * @param notes - String or NotesData object
 * @returns Metadata object or undefined
 */
export function getNotesMetadata(
  notes?: string | NotesData | Record<string, any>,
): Record<string, any> | undefined {
  if (notes == null || typeof notes !== 'object') {
    return undefined;
  }

  return (notes as any)?.metadata;
}
